import { formatDate } from '@betterment/core';
import { StepProps } from '@betterment/js-runtime';
import {
  Button,
  CenteredWorkflowLayout,
  ErrorText,
  Flex,
  FormikDateInput,
  FormikTextInput,
  Label,
  Text,
} from '@betterment/style-closet-react';
import { subYears } from 'date-fns';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { RequestValues } from './Controller';

import Footer from '../Shared/components/Footer';

const MAXIMUM_DATE_OF_BIRTH = subYears(new Date(), 18);
const MINIMUM_DATE_OF_BIRTH = subYears(new Date(), 115);

const ProspectInfoSchema = Yup.object<Partial<RequestValues>>().shape({
  firstName: Yup.string().required('Please enter their first name'),
  middleName: Yup.string().nullable(),
  lastName: Yup.string().required('Please enter their last name'),
  dateOfBirth: Yup.date()
    .required('Please enter their date of birth')
    .min(
      MINIMUM_DATE_OF_BIRTH,
      `Date of birth must be after ${formatDate(MINIMUM_DATE_OF_BIRTH, 'longOrdinalSlashed')}`
    )
    .max(
      MAXIMUM_DATE_OF_BIRTH,
      `Date of birth must be before ${formatDate(MAXIMUM_DATE_OF_BIRTH, 'longOrdinalSlashed')}`
    ),
});

const JointAccountInvitationProspectSecondaryInfo: React.FC<React.PropsWithChildren<StepProps<RequestValues>>> = ({
  goToNextStep,
  flowState,
}) => {
  const onSubmit = (values: RequestValues) => {
    goToNextStep({
      dateOfBirth: values.dateOfBirth,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
    });
  };

  return (
    <CenteredWorkflowLayout title="Joint Checking signup" heading="Tell us a bit more about who you're inviting">
      <Text as="dd" m="0" pb="8">
        It looks like they don't have a Betterment account. We'll need some additional information to get them started.
      </Text>
      <CenteredWorkflowLayout.Slot name="content">
        <Formik
          onSubmit={onSubmit}
          initialValues={flowState.values}
          validationSchema={ProspectInfoSchema}
          validateOnChange={false}
        >
          <Form>
            <Flex.Item flex="0 0 200px">
              <Label htmlFor="firstName">Legal first name</Label>
              <FormikTextInput name="firstName" aria-label="legal first name" />
              <ErrorMessage name="firstName" component={ErrorText} />
            </Flex.Item>
            <Flex.Item flex="0 0 200px">
              <Label htmlFor="middleName">Legal middle name (optional)</Label>
              <FormikTextInput name="middleName" aria-label="legal middle name" />
              <ErrorMessage name="middleName" component={ErrorText} />
            </Flex.Item>
            <Flex.Item flex="0 0 200px">
              <Label htmlFor="lastName">Legal last name</Label>
              <FormikTextInput name="lastName" aria-label="legal last name" />
              <ErrorMessage name="lastName" component={ErrorText} />
            </Flex.Item>
            <Flex.Item flex="0 0 200px">
              <Label htmlFor="dateOfBirth">Date of birth</Label>
              <FormikDateInput name="dateOfBirth" aria-label="date of birth" />
              <ErrorMessage name="dateOfBirth" component={ErrorText} />
            </Flex.Item>
            <CenteredWorkflowLayout.Actions>
              <Button type="submit" px="8">
                Continue
              </Button>
            </CenteredWorkflowLayout.Actions>
          </Form>
        </Formik>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default JointAccountInvitationProspectSecondaryInfo;
